import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import memoizeOne from "memoize-one";
import invariant from "tiny-invariant";

import { triggerPostMoveFlash } from "@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash";
import {
  type Instruction,
  type ItemMode,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import * as liveRegion from "@atlaskit/pragmatic-drag-and-drop-live-region";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ReactComponent as EmptyBox } from "../../../assets/icons/emptybox.svg";

import {
  getInitialTreeState,
  tree,
  type TreeItem as TreeItemType,
  treeStateReducer,
} from "./data/tree";
import {
  DependencyContext,
  TreeContext,
  type TreeContextValue,
} from "./treeItem/tree-context";
import TreeItem from "./treeItem";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddCircleRounded,
  DeleteRounded,
  SystemUpdateAltRounded,
  WebRounded,
} from "@mui/icons-material";
import IconPage from "./treeItem/iconPage";
import { NewItemModal } from "./treeItem/newItemModal";
import { useNotification } from "../../notificationProvider";
import api from "../../../services/api";
import { alpha } from "@mui/system";
import { Socket } from "socket.io-client";
import useStyles from "./styles";
import { ImportItemModal } from "./treeItem/importItemModal";

type CleanupFn = () => void;

function createTreeItemRegistry() {
  const registry = new Map<
    string,
    { element: HTMLElement; actionMenuTrigger: HTMLElement }
  >();

  const registerTreeItem = ({
    itemId,
    element,
    actionMenuTrigger,
  }: {
    itemId: string;
    element: HTMLElement;
    actionMenuTrigger: HTMLElement;
  }): CleanupFn => {
    registry.set(itemId, { element, actionMenuTrigger });
    return () => {
      registry.delete(itemId);
    };
  };

  return { registry, registerTreeItem };
}
interface TreeProps {
  items: TreeItemType[];
  setChanges: (changes: any) => void;
  socket: Socket;
  setUpdatePages: (value: boolean) => void;
  loading?: boolean;
  updatePages?: boolean;
}
export default function Tree(props: TreeProps) {
  const { items, setChanges, socket, loading, setUpdatePages } = props;

  invariant(items, "Tree items must be provided");
  const theme = useTheme();
  const styles = useStyles(theme);
  const [state, updateState] = useReducer(
    treeStateReducer,
    null,
    getInitialTreeState
  );
  const [type, setType] = useState<"PAI" | "FILHO">("PAI");
  const [status, setStatus] = useState<number>(2); // 2 = LIVRES
  const [expanded, setExpanded] = useState<
    "paginas-livres" | "lixeira" | false
  >("paginas-livres");
  const [openNewItemModal, setOpenNewItemModal] = useState(false);
  useEffect(() => {
    if (!data.length)
      updateState({
        type: "reset",
        data: items,
      });
  }, [items]);

  const { extractInstruction } = useContext(DependencyContext);
  const notify = useNotification();
  const [loadingImport, setLoadingImport] = useState(false);
  const [isOpenImportItemModal, setIsOpenImportItemModal] = useState(false);
  const [json, setJson] = useState<DashWithCardsGraphicsChildren>();
  const [arquiveName, setArquiveName] = useState<string>("");
  const [{ registry, registerTreeItem }] = useState(createTreeItemRegistry);
  const treeRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const freeRef = useRef<HTMLDivElement>(null);
  const dumpRef = useRef<HTMLDivElement>(null);
  const [heightDropRef, setHeightDropRef] = useState<number>(0);
  const { data, lastAction } = state;
  let lastStateRef = useRef<TreeItemType[]>(items);
  useEffect(() => {
    lastStateRef.current = data;
  }, [data]);

  useEffect(() => {
    if (lastAction === null) {
      return;
    }

    if (lastAction.type === "modal-move") {
      const parentName =
        lastAction.targetId === "" ? "the root" : `Item ${lastAction.targetId}`;

      // liveRegion.announce(
      //   `You've moved Item ${lastAction.itemId} to position ${
      //     lastAction.index + 1
      //   } in ${parentName}.`
      // );

      const { element, actionMenuTrigger } =
        registry.get(lastAction.itemId) ?? {};
      if (element) {
        triggerPostMoveFlash(element);
      }

      /**
       * Only moves triggered by the modal will result in focus being
       * returned to the trigger.
       */
      actionMenuTrigger?.focus();

      return;
    }

    if (lastAction.type === "instruction") {
      const { element } = registry.get(lastAction.itemId) ?? {};
      if (element) {
        triggerPostMoveFlash(element);
      }

      return;
    }
  }, [lastAction, registry]);

  useEffect(() => {
    return () => {
      liveRegion.cleanup();
    };
  }, []);

  /**
   * Returns the items that the item with `itemId` can be moved to.
   *
   * Uses a depth-first search (DFS) to compile a list of possible targets.
   */
  const getMoveTargets = useCallback(
    ({ itemId }: { itemId: string }) => {
      const data = lastStateRef.current;

      const targets = [];

      const searchStack = Array.from(data);
      while (searchStack.length > 0) {
        const node = searchStack.pop();

        if (!node) {
          continue;
        }

        /**
         * If the current node is the item we want to move, then it is not a valid
         * move target and neither are its children.
         */
        if (node.id === itemId) {
          continue;
        }

        /**
         * Draft items cannot have children.
         */
        if (node.type === "FILHO") {
          continue;
        }

        targets.push(node);

        node.children.forEach((childNode) => searchStack.push(childNode));
      }

      return targets;
    },
    [items]
  );

  const getParentsMoveTargets = useCallback(
    ({ itemId }: { itemId: string }) => {
      const data = lastStateRef.current;
      const recursiveParentes = (
        data: TreeItemType[],
        itemId: string
      ): TreeItemType[] => {
        return data.reduce((acc: TreeItemType[], item: TreeItemType) => {
          if (item.type === "PAI" && item.id !== itemId) {
            acc.push(item);
          }
          if (item.children.length > 0) {
            const children = recursiveParentes(item.children, itemId);
            if (children.length > 0) {
              acc.push(...children);
            }
          }
          return acc;
        }, []);
      };
      const targets = recursiveParentes(data, itemId);

      return targets;
    },
    [items]
  );
  const getChildrenOfItem = useCallback((itemId: string) => {
    const data = lastStateRef.current;

    /**
     * An empty string is representing the root
     */
    if (itemId === "") {
      return data;
    }

    const item = tree.find(data, itemId);
    invariant(item);
    return item.children;
  }, []);

  const context = useMemo<TreeContextValue>(
    () => ({
      dispatch: updateState,
      uniqueContextId: Symbol("unique-id"),
      // memoizing this function as it is called by all tree items repeatedly
      // An ideal refactor would be to update our data shape
      // to allow quick lookups of parents
      getPathToItem: memoizeOne(
        (targetId: string) =>
          tree.getPathToItem({ current: lastStateRef.current, targetId }) ?? []
      ),
      getMoveTargets,
      getChildrenOfItem,
      registerTreeItem,
      getParentsMoveTargets,
    }),
    [getChildrenOfItem, getMoveTargets, registerTreeItem, getParentsMoveTargets]
  );
  useEffect(() => {
    if (!dropRef?.current) return;
    const { offsetHeight } = dropRef.current;
    setHeightDropRef(offsetHeight);
    const cleanup = dropTargetForElements({
      element: dropRef.current,
      getData: ({ source }) => {
        return source.data;
      },
      canDrop: ({ source }) => {
        return source.data.type === "tree-item";
      },
      onDragEnter: () => {
        if (dropRef.current) {
          dropRef.current.style.backgroundColor = alpha(
            theme.palette.primary.main,
            0.1
          );
          dropRef.current.style.border = `1px dashed ${theme.palette.primary.main}`;
        }
      },
      onDragLeave: () => {
        if (dropRef.current) {
          dropRef.current.style.backgroundColor = "";
          dropRef.current.style.border = "none";
        }
      },
      onDrop: ({ source }) => {
        if (dropRef.current) {
          dropRef.current.style.backgroundColor = "";
          dropRef.current.style.border = "none";
        }
        if (source.data.id) {
          handleUpdateStatus({
            itemId: source.data.id as string,
            status: 1, // 2 = LIVRES
          });
          // handlSocketUpdatePosition(source.data.id as string, null, null, 0);
          // updateState({
          //   type: "remove-item-from-tree",
          //   itemId: source.data.id as string,
          // });
          // setExpanded("paginas-livres");
        }
      },
    });

    return () => {
      cleanup();
    };
  }, [updateState, context, dropTargetForElements, loading]);

  useEffect(() => {
    if (!treeRef?.current) return;

    return combine(
      monitorForElements({
        canMonitor: ({ source }) => source.data.type === "tree-item",
        onDrop(args) {
          const { location, source } = args;

          if (!location.current.dropTargets.length) {
            return;
          }

          if (source.data.type === "tree-item") {
            const itemId = source.data.id as string;

            const target = location.current.dropTargets[0];
            const targetId = target.data.id as string;

            let instruction: Instruction | null = extractInstruction(
              target.data
            );
            if (instruction?.type === "make-child") {
              const data = handleUpdatePosition({
                itemId,
                parentId: targetId,
                status: 1,
              });
            }
          }
        },
      })
    );
  }, [context, extractInstruction]);

  useEffect(() => {
    // console.log(boxRef.current);
    if (!freeRef?.current) return;

    const cleanup = dropTargetForElements({
      element: freeRef.current,
      getData: ({ source }) => {
        return source.data;
      },
      canDrop: ({ source }) => {
        return (
          source.data.type === "tree-item" && source.data.typeItem !== "PAI"
        );
      },
      onDragEnter: () => {
        if (freeRef.current) {
          freeRef.current.style.backgroundColor = alpha(
            theme.palette.primary.main,
            0.1
          );
          freeRef.current.style.border = `1px dashed ${theme.palette.primary.main}`;
        }
      },
      onDragLeave: () => {
        if (freeRef.current) {
          freeRef.current.style.backgroundColor = "";
          freeRef.current.style.border = "none";
        }
      },
      onDrop: ({ source }) => {
        if (freeRef.current) {
          freeRef.current.style.backgroundColor = "";
          freeRef.current.style.border = "none";
        }

        // console.log("Item solto:", source.data);
        if (source.data.id) {
          handleUpdateStatus({
            itemId: source.data.id as string,
            status: 2, // 2 = LIVRES
          });
          // handlSocketUpdatePosition(source.data.id as string, null, null, 0);
          // updateState({
          //   type: "remove-item-from-tree",
          //   itemId: source.data.id as string,
          // });
          // setExpanded("paginas-livres");
        }
      },
    });

    return () => {
      cleanup();
    };
  }, [updateState, context, dropTargetForElements, loading]);

  useEffect(() => {
    if (!dumpRef?.current) return;

    const cleanup = dropTargetForElements({
      element: dumpRef.current,
      getData: ({ source }) => {
        return source.data;
      },
      canDrop: ({ source }) => {
        return (
          source.data.type === "tree-item" && source.data.typeItem !== "PAI"
        );
      },
      onDragEnter: () => {
        if (dumpRef.current) {
          dumpRef.current.style.backgroundColor = alpha(
            theme.palette.primary.main,
            0.1
          );
          dumpRef.current.style.border = `1px dashed ${theme.palette.primary.main}`;
        }
      },
      onDragLeave: () => {
        if (dumpRef.current) {
          dumpRef.current.style.backgroundColor = "";
          dumpRef.current.style.border = "none";
        }
      },
      onDrop: ({ source }) => {
        if (dumpRef.current) {
          dumpRef.current.style.backgroundColor = "";
          dumpRef.current.style.border = "none";
        }

        // console.log("Item solto:", source.data.id);
        if (source.data.id) {
          handleUpdateStatus({
            itemId: source.data.id as string,
            status: 0, // 0 = LIXEIRA
          });
          //   handlSocketUpdatePosition(source.data.id as string, null, null, 0);
          //   updateState({
          //     type: "status",
          //     itemId: source.data.id as string,
          //     status: 0, // 0 = LIXEIRA
          //   });
          //   setExpanded("lixeira");
        }
      },
    });

    return () => {
      cleanup();
    };
  }, [updateState, context, dropTargetForElements, loading]);

  const treeViewItens = useMemo(() => {
    return data.filter((item) => {
      return item.status === 1;
    });
  }, [data]);
  const freePages = useMemo(() => {
    return data.filter((item) => {
      return item.type === "FILHO" && item.status === 2;
    });
  }, [data]);
  const dumpPages = useMemo(() => {
    return data.filter((item) => {
      return item.status === 0 && item.type === "FILHO";
    });
  }, [data]);
  async function handlSocketUpdatePosition(
    id: string,
    parentId?: string | null,
    status?: number | null
  ) {
    let data = { id, parentId, status };
    // console.log(parentId, id, "handlSocketUpdatePosition");

    return new Promise<any>((resolve, reject) => {
      try {
        socket.emit("updatePositionPages", data, (response: any) => {
          if (response.status === 201) {
            notify(null, "Elemento atualizado com sucesso!", "success");
            resolve(response.data);
          } else {
            notify(
              null,
              "Error: Não foi possível atualizar o elemento",
              "error"
            );
            reject(response);
          }
        });
      } catch (error) {
        console.error("Response error from socket:", error);
        notify(null, "Não foi possível se conectar com o servidor", "error");
        reject(error);
      }
    });
  }
  async function handleUpdatePosition({
    itemId,
    parentId,
    status,
  }: {
    itemId: string;
    parentId: string;
    status?: number;
  }) {
    const data = await handlSocketUpdatePosition(itemId, parentId, status);
    if (data && parentId) {
      updateState({
        type: "instruction",
        instruction: { type: "make-child", currentLevel: 0, indentPerLevel: 0 },
        itemId,
        targetId: parentId,
      });
    }
  }
  async function handleUpdateStatus({
    itemId,
    status,
  }: {
    itemId: string;
    status: number;
  }) {
    const data = await handlSocketUpdatePosition(itemId, null, status);

    if (data && status === 2) {
      // updateState({
      //   type: "remove-item-from-tree",
      //   itemId: itemId,
      // });
      updateState({
        type: "status",
        itemId: data.id,
        editadoEm: data?.updateAt,
        status: 2, // 2 = LIVRE
      });
      setExpanded("paginas-livres");
    }
    if (data && status === 0) {
      updateState({
        type: "status",
        itemId: itemId,
        editadoEm: data?.updateAt,
        status: 0, // 0 = LIXEIRA
      });
      setExpanded("lixeira");
    }
    if (data && status === 1) {
      updateState({
        type: "status",
        itemId: itemId,
        editadoEm: data?.updateAt,
        status: 1, // 1 = TREEVIEW
      });
    }
  }
  async function handleImportPageApi(
    json: Object,
    title: string,
    path: string
  ): Promise<any> {
    try {
      setLoadingImport(true);
      // console.log("handlePostPageApi", id, filters, title, parentId, position);
      const response = await api.post(
        "/pages/import",
        {
          json: { ...json, title, path },
          status: 2,
        }
        // { signal }
      );
      if (response.status === 200) {
        notify(null, "Página importada com sucesso", "success");
        return response.data;
      }
    } catch (error: any) {
      if (error.code && !error.response) {
        notify(error.code, error.message, "error");
      } else {
        notify(error.status, error?.response?.data.message, "error");
      }
      console.error(error);
    } finally {
      setLoadingImport(false);
    }
    return null;
  }

  const handlebuttonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    let json = {};
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async (e) => {
      try {
        json = JSON.parse(e.target?.result as string);

        if (json) {
          setJson(json as DashWithCardsGraphicsChildren);
          setArquiveName(file.name);
          setIsOpenImportItemModal(true);
          // const data = await handleImportPageApi(json);

          // if (!data) {
          //   notify(null, "Erro ao importar JSON", "error");
          //   return;
          // }
          // updateState({
          //   type: "create",
          //   itemId: data?.id,
          //   newItem: {
          //     id: data.id,
          //     title: data.title,
          //     type: "FILHO",
          //     status: data?.status,
          //     position: data?.position,
          //     children: [],
          //     isOpen: false,
          //   },
          // });
          setExpanded("paginas-livres");
        }
      } catch (error) {
        notify(null, "Erro ao importar JSON:", "error");
      }
    };

    reader.onerror = () => {
      notify(null, "Erro ao ler o arquivo", "error");
    };
  };
  // console.log("Tree items:", data);
  return (
    <TreeContext.Provider value={context}>
      <Box sx={styles.container}>
        <Box sx={styles.containerTree}>
          <Box sx={styles.headerTree}>
            <Typography variant="h6" sx={styles.title}>
              Preview da árvore de navegação
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleRounded />}
              onClick={() => {
                setType("PAI");
                setStatus(1);
                setOpenNewItemModal(true);
              }}
            >
              Novo Item
            </Button>
          </Box>
          <Box sx={styles.boxDropTreeAndTree}>
            <Box sx={styles.boxDropTree} id="tree" ref={dropRef}></Box>
            <Box sx={styles.tree(heightDropRef)} id="tree" ref={treeRef}>
              {treeViewItens.map((item, index, array) => {
                const type: ItemMode = (() => {
                  if (item.children.length && item.isOpen) {
                    return "expanded";
                  }

                  if (index === array.length - 1) {
                    return "last-in-group";
                  }

                  return "standard";
                })();

                return (
                  <>
                    <TreeItem
                      item={item}
                      level={0}
                      key={item.id}
                      mode={type}
                      index={index}
                      socket={socket}
                      setExpanded={setExpanded}
                      handleUpdateStatus={handleUpdateStatus}
                      // handlSocketUpdatePosition={handlSocketUpdatePosition}
                      setUpdatePages={setUpdatePages}
                    />
                  </>
                );
              })}
            </Box>
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem sx={styles.divider} />
        <Box sx={styles.containerAccordions}>
          {!!loading && (
            <>
              <Skeleton
                variant="rectangular"
                height={"400px"}
                width={"100%"}
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={"100px"}
                width={"100%"}
                sx={{ borderRadius: 1 }}
              />
            </>
          )}
          {!loading && (
            <Accordion
              sx={styles.accordion(expanded === "paginas-livres")}
              expanded={expanded === "paginas-livres"}
              onChange={() => {
                setExpanded(
                  expanded === "paginas-livres" ? false : "paginas-livres"
                );
              }}
              ref={freeRef}
              disableGutters
              // elevation={1}
            >
              <AccordionSummary
                sx={{
                  width: "100%",
                }}
                // expandIcon={<ExpandMoreRounded />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      flexDirection: "row",
                    }}
                  >
                    <Typography variant="h6" sx={styles.title2()}>
                      Páginas Livres
                    </Typography>
                    {freePages.length > 0 && expanded !== "paginas-livres" && (
                      <Box sx={styles.boxFreePages}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: "1rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {freePages.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="text"
                      startIcon={<SystemUpdateAltRounded />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                      }}
                      component={(props) => <label {...props} />}
                    >
                      <input
                        hidden
                        accept=".json"
                        type="file"
                        onChange={handlebuttonImport}
                      />
                      Importar
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<WebRounded />}
                      sx={{}}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        setType("FILHO");
                        setStatus(2);
                        setOpenNewItemModal(true);
                      }}
                    >
                      Nova página
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {freePages.length === 0 ? (
                    <Box sx={styles.boxEmpty}>
                      <EmptyBox style={styles.iconEmptyBox} />
                      <Typography variant="body1" sx={styles.subtitle}>
                        Nenhuma página livre encontrada. Crie uma nova página ou
                        importe um arquivo JSON. Você também pode arrastar e
                        soltar páginas aqui para movê-las para a lista de
                        páginas livres.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container columns={24} spacing={2}>
                      {freePages.length &&
                        freePages.map((page, index) => {
                          return (
                            <Grid key={page.id}>
                              <IconPage
                                index={index}
                                item={page}
                                level={0}
                                mode="standard"
                                socket={socket}
                                setExpanded={setExpanded}
                                handleUpdateStatus={handleUpdateStatus}
                              />
                            </Grid>
                          );
                        })}
                    </Grid>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
          {!loading && (
            <Accordion
              ref={dumpRef}
              sx={styles.accordion(expanded === "lixeira")}
              expanded={expanded === "lixeira"}
              onChange={() => {
                setExpanded(expanded === "lixeira" ? false : "lixeira");
              }}
              disableGutters
              // elevation={1}
            >
              <AccordionSummary
                // expandIcon={<ExpandMoreRounded />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography
                  variant="h6"
                  sx={styles.title2(theme.palette.error.main)}
                >
                  <DeleteRounded />
                  Lixeira
                </Typography>
                {dumpPages.length > 0 && expanded !== "lixeira" && (
                  <Box sx={styles.boxDumpPages}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: theme.palette.error.main,
                      }}
                    >
                      {dumpPages.length}
                    </Typography>
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ flexGrow: 1 }}>
                <Box sx={{ height: "100%" }}>
                  <Alert severity="warning" sx={styles.alert}>
                    <Typography variant="body2">
                      As páginas que foram movidas para a lixeira são exluídas 7
                      dias depois.
                    </Typography>
                  </Alert>
                  <Grid container columns={24} spacing={2}>
                    {dumpPages.map((page, index) => {
                      return (
                        <Grid key={page.id}>
                          <IconPage
                            index={index}
                            item={page}
                            level={0}
                            mode="standard"
                            socket={socket}
                            setExpanded={setExpanded}
                            handleUpdateStatus={handleUpdateStatus}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Box>
      <NewItemModal
        open={openNewItemModal}
        onClose={() => setOpenNewItemModal(false)}
        type={type}
        status={status}
      />
      <ImportItemModal
        open={isOpenImportItemModal}
        onClose={() => setIsOpenImportItemModal(false)}
        json={json as DashWithCardsGraphicsChildren}
        arquiveName={arquiveName}
      />
    </TreeContext.Provider>
  );
}
